import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Paper, Box, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { categoryOptions } from "../../../utils/Enums";
import { hasPermission } from "../../../context/AuthHelper";
import TableRefreshButton from "../../../components/TableRefreshButton/TableRefreshButton";

const getReferenceTypeLabel = (type, t) => {
  switch (type) {
    case categoryOptions.SUPPLIES:
      return t("supply");
    case categoryOptions.PRODUCTS:
    case categoryOptions.PRODUCT: // por si usás ambos
      return t("product");
    case categoryOptions.PREPARATIONS:
      return t("preparation");
    case categoryOptions.OTHER:
    default:
      return t("other");
  }
};

function CurrentStockTable({ 
  stocks = [], 
  loading, 
  paginationModel, 
  onPaginationModelChange, 
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
  searchTerm, 
  onSearchChange, 
  onRefresh,
  totalRows 
}) {
  const { t } = useTranslation();

  // 🔹 Asegurar que stocks sea siempre un array
  const safeStocks = Array.isArray(stocks) ? stocks : [];

  // 🔹 Saco todas las sucursales que vienen en branchStocks
  const allBranches = useMemo(() => {
    const branchMap = {};
    safeStocks.forEach((row) => {
      if (row.branchStocks && Array.isArray(row.branchStocks)) {
        row.branchStocks.forEach((b) => {
          branchMap[b.branchId] = b.branchName;
        });
      }
    });
    return Object.entries(branchMap).map(([branchId, branchName]) => ({
      branchId: parseInt(branchId, 10),
      branchName,
    }));
  }, [safeStocks]);


  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "productName",
        headerName: t("name"),
        flex: 2,
      },
      {
        field: "referenceType",
        headerName: t("type"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => getReferenceTypeLabel(params.value, t),
      },
      // columnas dinámicas de sucursal
      ...allBranches.map((branch) => ({
        field: `branch_${branch.branchId}`,
        headerName: branch.branchName,
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const bs = params.row.branchStocks.find(
            (b) => b.branchId === branch.branchId
          );
          return bs ? bs.stock.toFixed(1) : "—";
        },
      })),
      {
        field: "stockTotal",
        headerName: t("stock_total"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => params.value?.toFixed(1),
      },
      {
        field: "stockTotalSuggested",
        headerName: t("stock_total_suggested"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => params.value?.toFixed(1),
      },
      {
        field: "stockTotalMinimum",
        headerName: t("stock_total_minimum"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => params.value?.toFixed(1),
      },
      {
        field: "diffSuggested",
        headerName: t("diff_suggested"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => params.value?.toFixed(1),
      },
      {
        field: "diffMinimum",
        headerName: t("diff_minimum"),
        flex: 1,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => params.value?.toFixed(1),
      },
    ];
    return baseColumns;
  }, [allBranches, t]);

  return (
    <Paper sx={{ padding: 2 }}>
      {/* 🔹 Input de búsqueda arriba */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 1,
        }}
      >
        <TextField
          label={t("search")}
          placeholder={t("search_by_product_name")}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ width: "300px" }}
        />
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
          <TableRefreshButton loading={loading} onClick={onRefresh} />
        </Box>
      </Box>

      {/* Tabla */}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 1100 }}>
          <DataGrid
            loading={loading}
            rows={safeStocks}
            getRowId={(row) =>
              `${row.referenceType}-${row.referenceId}` // clave única
            }
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            sortingMode="server"
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            filterMode="server"
            pageSizeOptions={[10, 25, 50]}
            paginationMode="server"
            rowCount={totalRows}
            autoHeight
          />
        </Box>
      </Box>
    </Paper>
  );
}

export default CurrentStockTable;
